import { jsPDF } from "jspdf";

export default function generateMedicalReport(data, date) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const reportDate = date;
  let yPos = 10; // Set initial Y position

  // Function to wrap text within a column width (defined early for use in cover page)
  const wrapText = (doc, text, maxWidth) => {
    if (!text) return [''];

    const words = text.toString().split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = doc.getTextWidth(testLine);

      if (textWidth <= maxWidth - 4) { // Leave 4 units padding
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Single word is too long, truncate it
          lines.push(word.substring(0, Math.floor(word.length * maxWidth / textWidth)));
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  };

  // Function to add the cover page
  const addCoverPage = (doc, data) => {
    doc.setFontSize(18);
    doc.text("Medical Report", 148, yPos, { align: "center" });
    yPos += 10;

    doc.setFontSize(12);
    doc.text(`Date: ${reportDate}`, 250, 20);

    yPos += 15;

    // Define headers for the cover page
    // Optimized widths: increased Diet Restrict, Allergy RX, and Prescriptions for long text
    const headers = [
      { title: "Last Name", width: 30 },
      { title: "First Name", width: 30 },
      { title: "Medical Condition", width: 45 },
      { title: "Diet Restrict", width: 60 },
      { title: "Allergy RX", width: 65 },
      { title: "Prescriptions", width: 50 },
    ];

    // Draw table headers
    let x = 10;
    headers.forEach((header) => {
      doc.setDrawColor(0);
      doc.setFontSize(10);
      doc.setFillColor(200, 200, 200); // Light grey background
      doc.rect(x, yPos, header.width, 10, "FD");
      doc.setTextColor(0, 0, 0); // Black text
      doc.text(header.title, x + 2, yPos + 7);
      x += header.width;
    });

    yPos += 10;

    // Filter clients with medical conditions
    const clientsWithConditions = [];
    data.forEach((helicopter) => {
      helicopter.groups.forEach((group) => {
        group.clients.forEach((client) => {
        const customFields = client.customFields;
      // Check if there is any meaningful value in relevant fields (using correct API keys)
      const hasMedicalCondition = customFields["Medical Con"] && customFields["Medical Con"].trim().toLowerCase() !== 'no;';
      const hasPrescription = customFields["Prescriptions"] && customFields["Prescriptions"].trim().toLowerCase() !== 'no;';
      const hasAllergy = customFields["Allergy RX"] && customFields["Allergy RX"].trim().toLowerCase() !== 'no;';
      const hasDietRestriction = customFields["Diet Restrict"] && customFields["Diet Restrict"].trim().toLowerCase() !== 'no;';

          if (hasMedicalCondition || hasPrescription || hasAllergy || hasDietRestriction) {
            clientsWithConditions.push(client);
          }
        });
      });
    });
    console.log("Filtered Clients With Conditions:", clientsWithConditions);

    // Draw table rows with proper text wrapping and dynamic row height
    clientsWithConditions.forEach((client) => {
      // Calculate required row height based on text wrapping
      let maxRowHeight = 10; // Minimum row height

      headers.forEach((header) => {
        let text = "";
        switch (header.title) {
          case "Last Name":
            text = client.lastName || "N/A";
            break;
          case "First Name":
            text = client.firstName || "N/A";
            break;
          case "Medical Condition":
            text = client.customFields["Medical Con"] || "N/A";
            break;
          case "Diet Restrict":
            text = client.customFields["Diet Restrict"] || "N/A";
            break;
          case "Allergy RX":
            text = client.customFields["Allergy RX"] || "N/A";
            break;
          case "Prescriptions":
            text = client.customFields["Prescriptions"] || "N/A";
            break;
        }

        // Calculate lines needed for this cell
        const lines = wrapText(doc, text, header.width);
        const requiredHeight = Math.max(10, lines.length * 4 + 2);
        maxRowHeight = Math.max(maxRowHeight, requiredHeight);
      });

      // Check if we need a new page
      if (yPos + maxRowHeight > 180) {
        doc.addPage('landscape');
        yPos = 35; // Reset position after headers

        // Redraw headers on new page
        x = 10;
        headers.forEach((header) => {
          doc.setDrawColor(0);
          doc.setFontSize(10);
          doc.setFillColor(200, 200, 200);
          doc.rect(x, yPos, header.width, 10, "FD");
          doc.setTextColor(0, 0, 0);
          doc.text(header.title, x + 2, yPos + 7);
          x += header.width;
        });
        yPos += 10;
      }

      // Draw cells with calculated height
      x = 10;
      headers.forEach((header) => {
        let text = "";
        switch (header.title) {
          case "Last Name":
            text = client.lastName || "N/A";
            break;
          case "First Name":
            text = client.firstName || "N/A";
            break;
          case "Medical Condition":
            text = client.customFields["Medical Con"] || "N/A";
            break;
          case "Diet Restrict":
            text = client.customFields["Diet Restrict"] || "N/A";
            break;
          case "Allergy RX":
            text = client.customFields["Allergy RX"] || "N/A";
            break;
          case "Prescriptions":
            text = client.customFields["Prescriptions"] || "N/A";
            break;
        }
        doc.setDrawColor(0);
        doc.setFillColor(255, 255, 255); // White background
        doc.setFontSize(9);
        doc.rect(x, yPos, header.width, maxRowHeight, "FD");
        doc.setTextColor(0, 0, 0);

        // Use drawCellText for proper wrapping (need to define it before cover page)
        const lines = wrapText(doc, text, header.width);
        const lineHeight = 4;
        lines.forEach((line, index) => {
          if (index * lineHeight < maxRowHeight - 2) {
            doc.text(line, x + 2, yPos + 4 + (index * lineHeight));
          }
        });

        x += header.width;
      });
      yPos += maxRowHeight;
    });

    doc.addPage(); // Add a new page for the main content
  };

  // Add the cover page
  addCoverPage(doc, data);

  // Column definitions for consistent layout (matching API response keys)
  // Optimized widths: increased Diet Restrict, Allergy RX, and Prescriptions for long text
  const columns = [
    { header: "Last Name", width: 30, key: 'lastName' },
    { header: "First Name", width: 30, key: 'firstName' },
    { header: "Medical Condition", width: 45, key: 'Medical Con' },
    { header: "Diet Restrict", width: 60, key: 'Diet Restrict' },
    { header: "Allergy RX", width: 65, key: 'Allergy RX' },
    { header: "Prescriptions", width: 50, key: 'Prescriptions' },
  ];

  // wrapText function already defined above for use in cover page

  // Function to draw text in a cell with proper boundaries
  const drawCellText = (doc, text, x, y, width, height) => {
    const lines = wrapText(doc, text, width);
    const lineHeight = 3;

    // Draw each line of text
    lines.forEach((line, index) => {
      if (index * lineHeight < height - 1.5) { // Make sure text fits in cell height
        doc.text(line, x + 2, y + 3 + (index * lineHeight));
      }
    });

    return lines.length * lineHeight;
  };

  // Function to draw table headers
  const drawHeaders = (doc, y) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');

    let x = 10;
    columns.forEach(col => {
      doc.setDrawColor(0);
      doc.setFillColor(211, 211, 211);
      doc.rect(x, y, col.width, 5, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.text(col.header, x + 2, y + 3.5);
      x += col.width;
    });

    doc.setFont('helvetica', 'normal');
    return y + 5;
  };

  // Function to draw page header
  const drawPageHeader = (doc, helicopter, reportDate) => {
    doc.setFontSize(10);
    doc.text(`Heli #${helicopter.heliIndex} - ${helicopter.tripType || 'None'}`, 10, 10);
    doc.text(`Date: ${reportDate}`, 250, 10);
    doc.setFontSize(8);
    doc.text("Medical Report", 10, 14);
    return 18; // Return Y position after header
  };

  // Main content
  yPos = 10;
  data.forEach((helicopter, helicopterIndex) => {
    if (helicopterIndex > 0) {
      doc.addPage();
    }
    yPos = drawPageHeader(doc, helicopter, reportDate);

    helicopter.groups.forEach((group) => {
      // Check if we need space for group header + table header (about 15 units)
      if (yPos + 15 > 180) {
        doc.addPage();
        yPos = drawPageHeader(doc, helicopter, reportDate);
      }

      // Group header
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(200, 215, 1);
      doc.rect(10, yPos, 280, 4, "F");
      doc.setTextColor(0, 0, 0);
      doc.text(`Group #${group.groupIndex} (H${helicopter.heliIndex}G${group.groupIndex})`, 12, yPos + 3);
      yPos += 5;

      // Draw table headers
      yPos = drawHeaders(doc, yPos);
      yPos += 1;

      // Function to render a person row with proper text wrapping
      const addPersonRow = (person, isGuide = false) => {
        doc.setFontSize(7);

        // Calculate the row height needed based on text wrapping
        let maxRowHeight = 4; // Minimum row height

        // First pass: calculate required height for this row
        columns.forEach(col => {
          let cellText = '';
          if (col.key === 'lastName') {
            cellText = person.lastName || '';
          } else if (col.key === 'firstName') {
            cellText = person.firstName || '';
          } else {
            cellText = person.customFields ? (person.customFields[col.key] || '') : '';
          }

          const lines = wrapText(doc, cellText, col.width);
          const requiredHeight = Math.max(4, lines.length * 3 + 1.5);
          maxRowHeight = Math.max(maxRowHeight, requiredHeight);
        });

        // Check if we need a new page
        if (yPos + maxRowHeight > 180) {
          doc.addPage();
          yPos = drawPageHeader(doc, helicopter, reportDate);

          // Redraw group header and table headers on new page
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setFillColor(200, 215, 1);
          doc.rect(10, yPos, 280, 4, "F");
          doc.setTextColor(0, 0, 0);
          doc.text(`Group #${group.groupIndex} (H${helicopter.heliIndex}G${group.groupIndex})`, 12, yPos + 3);
          yPos += 5;
          yPos = drawHeaders(doc, yPos);
          yPos += 1;
        }

        // Set text color (blue for guides, black for clients)
        doc.setTextColor(isGuide ? 0 : 0, isGuide ? 0 : 0, isGuide ? 255 : 0);

        // Draw cell borders and content
        let x = 10;
        columns.forEach(col => {
          // Draw cell border
          doc.setDrawColor(200, 200, 200);
          doc.rect(x, yPos, col.width, maxRowHeight);

          // Get cell content
          let cellText = '';
          if (col.key === 'lastName') {
            cellText = person.lastName || '';
          } else if (col.key === 'firstName') {
            cellText = person.firstName || '';
          } else {
            cellText = person.customFields ? (person.customFields[col.key] || '') : '';
          }

          // Draw cell content
          drawCellText(doc, cellText, x, yPos, col.width, maxRowHeight);

          x += col.width;
        });

        yPos += maxRowHeight;
      };

      // Display guide details
      if (group.guide) {
        addPersonRow(group.guide, true);
      }

      // Display clients
      group.clients.forEach((client) => {
        addPersonRow(client, false);
      });

      yPos += 5; // Add some space after each group
    });
  });

  doc.save(`medical-report-${reportDate}.pdf`);
}